/**
 * ToolsView — Tools rail entry. Six top-level tabs:
 *   Quote Details · Option Chain · Trades · Calculator · Alerts · Calendar
 *
 * Plus a symbol header with a quick Buy/Sell, Add-to-Watchlist, Create-Alert,
 * and (under Quote Details) a sub-tab strip:
 *   Quote Details · Analyst Reports · Fundamentals · Option Stats
 *
 * All data is read from `symbol-data.ts` (deterministic per symbol).
 */
import { useMemo, useState } from 'react';
import {
  optionStatsFor, quoteDetailsFor, fundamentalsFor, analystReportsFor, calendarFor,
} from '../lib/symbol-data';
import {
  fundamentalsRichFor, companyDescription, formatMetricValue,
  type Aggregation, type MetricSeries,
} from '../lib/fundamentals-rich';
import { koboToNaira } from '../lib/format';
import { StockAlerts } from './StockAlerts';
import { FXAlerts } from './FXAlerts';

type TopTab = 'Quote Details' | 'Option Chain' | 'Trades' | 'Calculator' | 'FX Alerts' | 'Stock Alerts' | 'Calendar';
type SubTab = 'Quote Details' | 'Analyst Reports' | 'Fundamentals' | 'Option Stats';

interface Props {
  symbol: string;
  symbolLongName?: string;
  mark: number;
  pctChange: number;
  onJumpToTrade?: (side: 'BUY' | 'SELL') => void;
  onAddToWatchlist?: (symbol: string) => void;
}

export function ToolsView({
  symbol, symbolLongName, mark, pctChange,
  onJumpToTrade, onAddToWatchlist,
}: Props) {
  const [topTab, setTopTab] = useState<TopTab>('Quote Details');
  const [subTab, setSubTab] = useState<SubTab>('Option Stats');

  const quote = useMemo(() => quoteDetailsFor(symbol, mark), [symbol, mark]);
  const stats = useMemo(() => optionStatsFor(symbol, mark), [symbol, mark]);
  const fund  = useMemo(() => fundamentalsFor(symbol, mark), [symbol, mark]);
  const reports = useMemo(() => analystReportsFor(symbol), [symbol]);
  const cal = useMemo(() => calendarFor(symbol), [symbol]);

  const change = +(mark - quote.prevClose).toFixed(2);
  const changeNeg = change < 0;

  return (
    <div className="tv">
      <div className="tv-tabs" role="tablist">
        {(['Quote Details','Option Chain','Trades','Calculator','FX Alerts','Stock Alerts','Calendar'] as TopTab[]).map((t) => (
          <button key={t} role="tab" aria-selected={topTab === t}
                  className={`tv-tab ${topTab === t ? 'active' : ''}`}
                  onClick={() => setTopTab(t)}>{t}</button>
        ))}
      </div>

      <div className="tv-symhead">
        <div className="tv-symhead-left">
          <h1 className="tv-sym">{symbol} <span className="tv-name">{symbolLongName ?? ''}</span></h1>
          <div className="tv-actions">
            <button className="tv-link" onClick={() => onAddToWatchlist?.(symbol)}>+ Add to Watchlist</button>
          </div>
        </div>
        <div className="tv-symhead-right">
          <div className="tv-bid">
            <div className="px">{quote.bid.toFixed(2)}</div>
            <div className="muted">Bid size: {quote.bidSize}</div>
          </div>
          <button className="tv-sell" onClick={() => onJumpToTrade?.('SELL')}>Sell</button>
          <div className="tv-mark">
            <span className={`tv-px ${changeNeg ? 'down' : 'up'}`}>{mark.toFixed(2)}</span>
            <span className={changeNeg ? 'down' : 'up'}>{change >= 0 ? '+' : ''}{change.toFixed(2)} ({pctChange >= 0 ? '+' : ''}{pctChange.toFixed(2)}%)</span>
          </div>
          <button className="tv-buy" onClick={() => onJumpToTrade?.('BUY')}>Buy</button>
          <div className="tv-ask">
            <div className="px">{quote.ask.toFixed(2)}</div>
            <div className="muted">Ask size: {quote.askSize}</div>
          </div>
        </div>
      </div>

      {topTab === 'Quote Details' && (
        <>
          <div className="tv-subtabs" role="tablist">
            {(['Quote Details','Analyst Reports','Fundamentals','Option Stats'] as SubTab[]).map((t) => (
              <button key={t} role="tab" aria-selected={subTab === t}
                      className={`tv-subtab ${subTab === t ? 'active' : ''}`}
                      onClick={() => setSubTab(t)}>{t}</button>
            ))}
          </div>
          <div className="tv-body">
            {subTab === 'Quote Details' && <QuoteDetailsPanel q={quote} />}
            {subTab === 'Analyst Reports' && <AnalystPanel reports={reports} />}
            {subTab === 'Fundamentals' && <FundamentalsRich symbol={symbol} />}
            {subTab === 'Option Stats' && <OptionStatsPanel s={stats} />}
          </div>
        </>
      )}

      {topTab === 'Option Chain' && (
        <div className="tv-body"><div className="tv-card" style={{ gridColumn: '1 / -1' }}>
          <div className="muted" style={{ fontSize: 12 }}>NGX does not list listed options yet.
            Once SEC approves the NGX derivatives roadmap, the option chain (calls + puts grid by expiry/strike) will render here, with one-click order entry into the existing OrderTicket pipeline.</div>
        </div></div>
      )}

      {topTab === 'Trades' && <TradesPanel symbol={symbol} mark={mark} />}
      {topTab === 'Calculator' && <CalculatorPanel symbol={symbol} mark={mark} />}
      {topTab === 'FX Alerts' && (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <FXAlerts />
        </div>
      )}
      {topTab === 'Stock Alerts' && (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <StockAlerts />
        </div>
      )}
      {topTab === 'Calendar' && <CalendarPanel events={cal} />}
    </div>
  );
}

// ===== sub-panels =====
function QuoteDetailsPanel({ q }: { q: ReturnType<typeof quoteDetailsFor> }) {
  const Row = ({ k, v }: { k: string; v: string | number }) => (
    <div className="tv-kv"><span className="tv-k">{k}</span><span className="tv-v mono">{v}</span></div>
  );
  return (
    <>
      <div className="tv-card"><Row k="Open" v={q.open.toFixed(2)} /><Row k="High" v={q.high.toFixed(2)} /><Row k="Low" v={q.low.toFixed(2)} /></div>
      <div className="tv-card"><Row k="Prev. Close" v={q.prevClose.toFixed(2)} /><Row k="VWAP" v={q.vwap.toFixed(2)} /><Row k="Volume" v={q.volume.toLocaleString('en-NG')} /></div>
      <div className="tv-card"><Row k="52W High" v={q.range52W.hi.toFixed(2)} /><Row k="52W Low" v={q.range52W.lo.toFixed(2)} /><Row k="Market Cap" v={koboToNaira(String(q.marketCap))} /></div>
    </>
  );
}

function AnalystPanel({ reports }: { reports: ReturnType<typeof analystReportsFor> }) {
  return (
    <div className="tv-card" style={{ gridColumn: '1 / -1' }}>
      <table className="tv-table">
        <thead><tr><th>Firm</th><th>Date</th><th>Rating</th><th style={{ textAlign: 'right' }}>Target</th><th>Note</th></tr></thead>
        <tbody>
          {reports.map((r, i) => (
            <tr key={i}>
              <td>{r.firm}</td>
              <td className="mono">{r.date}</td>
              <td><span className={`tv-rating ${ratingClass(r.rating)}`}>{r.rating}</span></td>
              <td className="mono" style={{ textAlign: 'right' }}>₦{r.targetPrice.toFixed(2)}</td>
              <td className="muted">{r.note}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
function ratingClass(r: string): string {
  if (r === 'Buy' || r === 'Overweight') return 'up';
  if (r === 'Sell' || r === 'Underweight') return 'down';
  return 'mute';
}

function FundamentalsPanel({ f }: { f: ReturnType<typeof fundamentalsFor> }) {
  const Row = ({ k, v }: { k: string; v: string | number }) => (
    <div className="tv-kv"><span className="tv-k">{k}</span><span className="tv-v mono">{v}</span></div>
  );
  return (
    <>
      <div className="tv-card"><Row k="P/E" v={f.pe.toFixed(2)} /><Row k="EPS" v={`₦${f.eps.toFixed(2)}`} /><Row k="Book Value" v={`₦${f.bookValue.toFixed(2)}`} /></div>
      <div className="tv-card"><Row k="Dividend Yield" v={`${f.divYieldPct.toFixed(2)}%`} /><Row k="Beta" v={f.beta.toFixed(2)} /><Row k="Shares Out" v={`${f.sharesOutMillions.toLocaleString('en-NG')}M`} /></div>
      <div className="tv-card"><Row k="Sector" v={f.sector} /><Row k="Industry" v={f.industry} /><Row k="Market Cap" v={koboToNaira(String(f.marketCap))} /></div>
    </>
  );
}

function OptionStatsPanel({ s }: { s: ReturnType<typeof optionStatsFor> }) {
  return (
    <>
      <Stat label="Implied Volatility"     value={`${s.impliedVolatility.toFixed(2)}%`}    color="cyan" />
      <Stat label="Historical Volatility"  value={`${s.historicalVolatility.toFixed(2)}%`} />
      <Stat label="IV Percentile"          value={`${s.ivPercentile}%`}                    color="green" />
      <Stat label="Put/Call Ratio"         value={s.putCallRatio.toFixed(2)}                color="green" />
      <Stat label="Total Open Interest"    value={s.totalOpenInterest.toLocaleString('en-NG')} />
      <Stat label="Implied Move"           value={`${s.impliedMovePct.toFixed(1)}%`}       color="orange" />
    </>
  );
}

function Stat({ label, value, color }: { label: string; value: string; color?: 'cyan' | 'green' | 'orange' }) {
  return (
    <div className="tv-stat">
      <div className="tv-stat-k muted">{label}</div>
      <div className={`tv-stat-v ${color ?? ''}`}>{value}</div>
    </div>
  );
}

function TradesPanel({ symbol, mark }: { symbol: string; mark: number }) {
  const trades = useMemo(() => Array.from({ length: 25 }, (_, i) => {
    const ago = i * 9_000 + (i * 113 % 5000);
    const px = +(mark + ((i * 31 % 11) - 5) * mark * 0.0008).toFixed(2);
    const qty = (i * 43 % 90) + 10;
    const side = (i & 1) ? 'BUY' : 'SELL';
    return { t: Date.now() - ago, px, qty, side };
  }), [symbol, mark]);
  return (
    <div className="tv-body"><div className="tv-card" style={{ gridColumn: '1 / -1' }}>
      <table className="tv-table mono">
        <thead><tr><th>Time</th><th>Side</th><th style={{ textAlign: 'right' }}>Price</th><th style={{ textAlign: 'right' }}>Qty</th></tr></thead>
        <tbody>
          {trades.map((t, i) => (
            <tr key={i}>
              <td>{new Date(t.t).toISOString().slice(11, 19)}</td>
              <td><span className={t.side === 'BUY' ? 'up' : 'down'}>{t.side}</span></td>
              <td style={{ textAlign: 'right' }}>{t.px.toFixed(2)}</td>
              <td style={{ textAlign: 'right' }}>{t.qty}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div></div>
  );
}

function CalculatorPanel({ symbol, mark }: { symbol: string; mark: number }) {
  const [qty, setQty] = useState(100);
  const [price, setPrice] = useState(mark.toFixed(2));
  const [target, setTarget] = useState((mark * 1.05).toFixed(2));
  const consideration = qty * Number(price);
  const gain = qty * (Number(target) - Number(price));
  const pctGain = Number(price) === 0 ? 0 : ((Number(target) - Number(price)) / Number(price)) * 100;
  return (
    <div className="tv-body">
      <div className="tv-card">
        <div className="tv-kv"><span className="tv-k">Symbol</span><span className="tv-v mono">{symbol}</span></div>
        <div className="tv-kv"><span className="tv-k">Quantity</span>
          <input className="tv-inp" type="number" value={qty} onChange={(e) => setQty(Math.max(1, Number(e.target.value)))} /></div>
        <div className="tv-kv"><span className="tv-k">Entry Price</span>
          <input className="tv-inp" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} /></div>
        <div className="tv-kv"><span className="tv-k">Target Price</span>
          <input className="tv-inp" inputMode="decimal" value={target} onChange={(e) => setTarget(e.target.value)} /></div>
      </div>
      <div className="tv-card">
        <div className="tv-kv"><span className="tv-k">Consideration</span><span className="tv-v mono">₦{consideration.toLocaleString('en-NG', { maximumFractionDigits: 2 })}</span></div>
        <div className="tv-kv"><span className="tv-k">Gross P/L</span>
          <span className={`tv-v mono ${gain >= 0 ? 'up' : 'down'}`}>₦{gain.toLocaleString('en-NG', { maximumFractionDigits: 2 })}</span></div>
        <div className="tv-kv"><span className="tv-k">Return %</span>
          <span className={`tv-v mono ${pctGain >= 0 ? 'up' : 'down'}`}>{pctGain >= 0 ? '+' : ''}{pctGain.toFixed(2)}%</span></div>
      </div>
      <div className="tv-card">
        <div className="muted" style={{ fontSize: 11 }}>NGX fees can be previewed in the Order Ticket — switch to Trade to see the full breakdown (Brokerage, VAT, SEC, CSCS, Stamp Duty, Terminal Fee).</div>
      </div>
    </div>
  );
}


function CalendarPanel({ events }: { events: ReturnType<typeof calendarFor> }) {
  return (
    <div className="tv-body"><div className="tv-card" style={{ gridColumn: '1 / -1' }}>
      <table className="tv-table">
        <thead><tr><th>Date</th><th>Event</th><th>Details</th></tr></thead>
        <tbody>
          {events.map((e, i) => (
            <tr key={i}>
              <td className="mono">{e.date}</td>
              <td><span className={`tv-event ${e.kind.toLowerCase()}`}>{e.kind}</span></td>
              <td className="muted">{e.details}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div></div>
  );
}


// ===== Rich Fundamentals (5xN sparkline grid) =====
function FundamentalsRich({ symbol }: { symbol: string }) {
  const [agg, setAgg] = useState<Aggregation>('Quarterly');
  const [vis, setVis] = useState<'line' | 'bar'>('line');
  const [showFull, setShowFull] = useState(false);
  const desc = companyDescription(symbol);
  const series = fundamentalsRichFor(symbol, agg);
  const truncated = desc.length > 160 && !showFull ? desc.slice(0, 160) + '...' : desc;
  return (
    <div className="fr">
      <div className="fr-desc">
        {truncated}
        {desc.length > 160 && (
          <button className="tv-link" style={{ marginLeft: 8 }} onClick={() => setShowFull((v) => !v)}>
            {showFull ? 'Show Less' : 'Show More'}
          </button>
        )}
      </div>
      <div className="fr-toolbar">
        <div className="fr-agg">
          <span className="muted">Aggregation:</span>
          <select className="tv-inp" value={agg} onChange={(e) => setAgg(e.target.value as Aggregation)}>
            <option>Quarterly</option>
            <option>Annual</option>
            <option>TTM</option>
          </select>
        </div>
        <div className="fr-vis">
          <button className={`fr-vbtn ${vis === 'bar' ? 'active' : ''}`} onClick={() => setVis('bar')} aria-label="Bar">▤</button>
          <button className={`fr-vbtn ${vis === 'line' ? 'active' : ''}`} onClick={() => setVis('line')} aria-label="Line">∿</button>
        </div>
      </div>
      <div className="fr-grid">
        {series.map((m) => (
          <MetricCard key={m.name} m={m} kind={vis} />
        ))}
      </div>
    </div>
  );
}

function MetricCard({ m, kind }: { m: MetricSeries; kind: 'line' | 'bar' }) {
  const W = 200, H = 80, padX = 18, padY = 22;
  const innerW = W - padX * 2; const innerH = H - padY - 14;
  const min = Math.min(...m.values); const max = Math.max(...m.values);
  const span = max - min || 1;
  const x = (i: number) => padX + (m.values.length === 1 ? innerW / 2 : (i / (m.values.length - 1)) * innerW);
  const y = (v: number) => padY + (1 - (v - min) / span) * innerH;
  const last = m.values[m.values.length - 1] ?? 0;
  const first = m.values[0] ?? 0;
  const trendUp = last >= first;
  const tagColor = trendUp ? '#3ddc84' : '#ff6b6b';
  const lineColor = '#3dd6ff';
  return (
    <div className="fr-card">
      <div className="fr-card-h">{m.name}</div>
      <svg viewBox={`0 0 ${W} ${H}`} className="fr-svg" preserveAspectRatio="none">
        {/* end-of-series highlighted value label */}
        <text x={x(m.values.length - 1) + 8} y={y(last) + 3} fontSize="10" fontWeight="800" fill={tagColor} textAnchor="start">
          {formatMetricValue(last, m.format)}
        </text>
        {/* series */}
        {kind === 'line' ? (
          <>
            <path
              d={m.values.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(v)}`).join(' ')}
              fill="none" stroke={lineColor} strokeWidth="1.6"
            />
            {m.values.map((v, i) => (
              <circle key={i} cx={x(i)} cy={y(v)} r={2.5} fill={lineColor} />
            ))}
            <circle cx={x(m.values.length - 1)} cy={y(last)} r={3.5} fill={tagColor} stroke="#0a1120" strokeWidth=".8" />
          </>
        ) : (
          m.values.map((v, i) => {
            const bw = innerW / m.values.length * 0.6;
            const bx = x(i) - bw / 2;
            const by = y(v); const bh = (padY + innerH) - by;
            return <rect key={i} x={bx} y={by} width={bw} height={Math.max(1, bh)} fill={lineColor} opacity=".75" />;
          })
        )}
        {/* per-point labels above each marker */}
        {m.values.map((v, i) => (
          <text key={'lbl' + i} x={x(i)} y={y(v) - 6} fontSize="9" fill="#dfe6f2" textAnchor="middle">
            {formatMetricValue(v, m.format)}
          </text>
        ))}
        {/* x-axis labels */}
        {m.labels.map((lab, i) => (
          <text key={'x' + i} x={x(i)} y={H - 2} fontSize="8" fill="#7a8cab" textAnchor="middle">{lab}</text>
        ))}
      </svg>
    </div>
  );
}

